
import { defineComponent } from "vue";
import TimerBox from "@/components/TimerBox.vue";
import GameWrapper from "@/components/GameWrapper.vue";
import Timer from "@/data/Timer";
import Task from "@/data/Task";
import type { Solution } from "@/data/Task";
import type { ActionButtonOptions, GameStep } from "@/data/types";
import { GAME_STATE, OPERATOR_COLLECTION } from "@/data/constants";
import GameStepIndicator from "@/components/GameStepIndicator.vue";

const actionButtons: ActionButtonOptions[] = [
  {
    name: "next",
    alternative: "space",
    code: "Space",
    label: "Next",
    clickEvent: {
      event: "next",
    },
    isFullSize: true,
  },
  {
    name: "solution1",
    alternative: "←",
    code: "ArrowLeft",
    label: "",
    clickEvent: {
      event: "commit-solution",
      value: 0,
    },
    isFullSize: false,
  },
  {
    name: "solution2",
    alternative: "→",
    code: "ArrowRight",
    label: "",
    clickEvent: {
      event: "commit-solution",
      value: 1,
    },
    isFullSize: false,
    hasExtraBorder: true,
  },
];

export default defineComponent({
  name: "ChainSolver",

  components: {
    TimerBox,
    GameWrapper,
    GameStepIndicator,
  },

  data() {
    return {
      actionButtons,
      gamePhases: [] as GameStep[],
      gameSteps: [] as GameStep[],
      gameTimer: new Timer() as Timer,
      gameTimeThreshold: 115,
      isGameOver: true,
      phaseIndex: 0,
      restartCounter: 0,
      solutions: [] as Array<Solution>,
      stepIndex: 0,
      task: new Task(8, {
        isStepped: true,
        operators: OPERATOR_COLLECTION.BASIC,
      }) as Task,
    };
  },

  computed: {
    currentActionButtons(): ActionButtonOptions[] {
      if (this.isGameOver) return [];
      if (!this.isFinalStep) {
        return this.actionButtons.slice(0, 1);
      }
      return this.actionButtons.slice(-2);
    },

    gamePoints(): number {
      let points = 0;
      this.gamePhases.forEach((phase) => {
        points += phase.state === GAME_STATE.DONE ? 1 : 0;
      });

      if (points > 6) {
        let overTime = this.gameTimer.seconds - this.gameTimeThreshold;
        let timerPoints =
          overTime < 0
            ? 5
            : 5 - (Math.ceil(overTime / 10) > 5 ? 5 : Math.ceil(overTime / 10));
        points += timerPoints;
      }
      return points;
    },

    isFinalStep(): boolean {
      return this.stepIndex >= this.gameSteps.length;
    },
  },

  methods: {
    resetGamePhases() {
      this.gamePhases = Array.from({ length: 10 }, (x, i) => ({
        state: i === 0 ? GAME_STATE.UNDECIDED : GAME_STATE.PENDING,
        name: `phase-${i}`,
      }));
    },

    resetGameSteps() {
      let taskLength = 8;
      if (this.phaseIndex > 4 && this.phaseIndex < 8) {
        taskLength = 10;
      } else if (this.phaseIndex >= 8) {
        taskLength = 12;
      }
      this.task.new(taskLength);
      this.solutions = this.task.getPossibleSolutions(2);
      this.actionButtons[1].label = `${this.solutions[0].value}`;
      this.actionButtons[2].label = `${this.solutions[1].value}`;

      this.gameSteps = Array.from({ length: taskLength }, (x, i) => ({
        state: i === 0 ? GAME_STATE.UNDECIDED : GAME_STATE.PENDING,
        name: `step-${i}`,
      }));
    },

    restart() {
      this.restartCounter += 1;
      this.phaseIndex = 0;
      this.stepIndex = 0;
      this.resetGamePhases();
      this.resetGameSteps();
      this.gameTimer.reset();
      this.isGameOver = false;
    },

    startGameTimer() {
      this.gameTimer.start();
    },

    nextStep() {
      if (this.isFinalStep) return;
      this.stepIndex += 1;
      if (this.stepIndex === this.gameSteps.length) {
        this.gameSteps[this.stepIndex - 1].state = GAME_STATE.DONE;
        return;
      }
      if (this.stepIndex > 0) {
        this.gameSteps[this.stepIndex - 1].state = GAME_STATE.DONE;
      }
      this.gameSteps[this.stepIndex].state = GAME_STATE.UNDECIDED;
    },

    commitSolution(index: number) {
      if (!this.isFinalStep) return;
      this.phaseIndex += 1;
      this.stepIndex = -1;
      this.nextStep();
      if (this.phaseIndex === 0) return;
      if (this.phaseIndex === this.gamePhases.length) {
        this.gamePhases[this.phaseIndex - 1].state = this.solutions[index]
          .isValid
          ? GAME_STATE.DONE
          : GAME_STATE.ERROR;
        this.endGame();
        return;
      }
      this.gamePhases[this.phaseIndex - 1].state = this.solutions[index].isValid
        ? GAME_STATE.DONE
        : GAME_STATE.ERROR;
      this.gamePhases[this.phaseIndex].state = GAME_STATE.UNDECIDED;
      this.resetGameSteps();
    },

    endGame() {
      this.gameSteps = [];
      this.gameTimer.stop();
      this.isGameOver = true;
    },
  },
});
